import type { Entry, EntryFieldTypes, EntrySkeletonType } from 'contentful';
import {
  ContentfulComponent,
  Cta,
  MediaAsset,
  Sys,
  HyperlinkedMediaProps,
  AspectRatio,
  ColorField,
  DocumentWithEmbeddedEntries,
  WithSectionName,
  WithStatsigFlag,
  WithTypename,
} from 'libs/contentful/types';
import { YouTubeEmbedProps } from 'components/shared/YouTubeEmbed/types';
import { Margins, Colorways, ImageWidth } from 'types/constants';
import { RecursivePartial } from 'utils/recursivePartial';
import { VideoItemReference } from '@/types/sharedComponents';
import type { AspectRatioSkeleton } from 'components/HeaderLogo/types';
import type { CtaSkeleton } from 'components/shared/CTA/types';
import type { VideoItemSkeleton } from 'components/VideoPlayer/types';
import type { HyperlinkedMediaSkeleton } from 'components/shared/HyperlinkedMedia/types';

export enum CardVariant {
  Icon = 'Icon',
  Image = 'Image',
  Text = 'Text',
  Video = 'Video',
}

export enum CardStyle {
  Stepped = 'Stepped',
  Paginated = 'Paginated',
  None = 'None',
}

export enum HyperlinkedImagesLocation {
  Top = 'Top',
  Bottom = 'Bottom',
}

export interface CardWrapperClassNames {
  wrapper: string;
  header: string;
  content: string;
}

export interface CardContentClassNames {
  wrapper: string;
  header: {
    wrapper: string;
    eyebrow: string;
    headline: string;
  };

  copy: string;
}

export interface CardContentProps {
  sys: Sys;

  showFiveStarEyebrow?: boolean;
  showChimeLogo?: boolean;
  eyebrow?: DocumentWithEmbeddedEntries;

  headline?: DocumentWithEmbeddedEntries;
  copy?: DocumentWithEmbeddedEntries;

  className?: RecursivePartial<CardContentClassNames>;
  testId?: string;
}

export interface CardCTAClassNames {
  wrapper: string;
}

export interface CardCTAProps {
  sys?: Sys;

  cta: Cta;
  cardVariant: CardVariant;
  className?: RecursivePartial<CardCTAClassNames>;
}

export interface CardClassNames {
  cardWrapper: CardWrapperClassNames;
  cardContent: CardContentClassNames;
}

export interface IconVariantClassNames extends CardClassNames {
  icon: string;
  eyebrow: string;
  cta: string;

  hyperlinkedImages: string;
}

export interface ImageVariantClassNames extends CardClassNames {
  media: {
    wrapper: string;
    content: string;
  };

  hyperlinkedImages: string;
}

export type TextVariantClassNames = CardClassNames;

export interface VideoVariantClassNames extends CardClassNames {
  transitions: {
    playing: string;
    paused: string;
  };
  media: {
    wrapper: string;
    content: string;
  };
  preview: {
    label: string;
  };
}

export interface CardCarouselClassNames {
  wrapper: string;
  carousel: string;
  pagination: string;
  cardGrid: string;
  iconVariant: RecursivePartial<IconVariantClassNames>;
  imageVariant: RecursivePartial<ImageVariantClassNames>;
  textVariant: RecursivePartial<TextVariantClassNames>;
  videoVariant: RecursivePartial<VideoVariantClassNames>;
}

export interface CardWrapperProps {
  sys?: Sys;

  cardStyle?: CardStyle;

  index?: number;
  total?: number;

  color: Colorways;
  children: React.ReactNode;
  className?: RecursivePartial<CardWrapperClassNames>;

  testId?: string;
}

export interface CardProps extends ContentfulComponent {
  index: number;
  total: number;

  variant: CardVariant;

  cardStyle: CardStyle;
  color: Colorways;

  headline?: DocumentWithEmbeddedEntries;
  copy?: DocumentWithEmbeddedEntries;
}

export interface IconVariantProps extends CardProps {
  variant: CardVariant.Icon;
  iconImage?: MediaAsset;

  showFiveStarEyebrow?: boolean;
  showChimeLogo?: boolean;
  eyebrow?: DocumentWithEmbeddedEntries;

  hyperlinkedImages?: {
    items: HyperlinkedMediaProps[];
  };

  hyperlinkedImagesLocation?: HyperlinkedImagesLocation;

  cta?: Cta;
  className?: RecursivePartial<IconVariantClassNames>;
}

export interface ImageVariantProps extends CardProps {
  variant: CardVariant.Image;
  desktopMedia?: MediaAsset;
  mobileMedia?: MediaAsset;
  desktopAspectRatio?: AspectRatio;
  mobileAspectRatio?: AspectRatio;

  showFiveStarEyebrow?: boolean;
  showChimeLogo?: boolean;
  eyebrow?: DocumentWithEmbeddedEntries;

  hyperlinkedImages?: {
    items: HyperlinkedMediaProps[];
  };

  hyperlinkedImagesLocation?: HyperlinkedImagesLocation;

  imageSize?: ImageWidth;
  className?: RecursivePartial<ImageVariantClassNames>;
}

export interface TextVariantProps extends CardProps {
  variant: CardVariant.Text;
  className?: RecursivePartial<TextVariantClassNames>;
}

export interface VideoVariantProps extends CardProps {
  variant: CardVariant.Video;
  video?: VideoItemReference | YouTubeEmbedProps;
  className?: RecursivePartial<VideoVariantClassNames>;
}

export type CardVariantProps = IconVariantProps | ImageVariantProps | TextVariantProps | VideoVariantProps;

export type CarouselPaginationThresholdProp = (isAboveLg: boolean, isAboveMd: boolean) => number;

export interface YouTubeEmbedFields {
  youTubeVideoUrl: EntryFieldTypes.Symbol;
}

export type YouTubeEmbedSkeleton = EntrySkeletonType<YouTubeEmbedFields, 'youTubeEmbed'>;

export interface CardFields {
  variant: EntryFieldTypes.Symbol<CardVariant>;
  imageSize: EntryFieldTypes.Symbol<ImageWidth>;
  hyperlinkedImagesLocation: EntryFieldTypes.Symbol<HyperlinkedImagesLocation>;
  color?: EntryFieldTypes.Object<ColorField>;
  showFiveStarEyebrow?: EntryFieldTypes.Boolean;
  showChimeLogo?: EntryFieldTypes.Boolean;
  eyebrow?: EntryFieldTypes.RichText;
  headline?: EntryFieldTypes.RichText;
  copy?: EntryFieldTypes.RichText;
  cta?: EntryFieldTypes.EntryLink<CtaSkeleton>;
  iconImage?: EntryFieldTypes.AssetLink;
  desktopMedia?: EntryFieldTypes.AssetLink;
  mobileMedia?: EntryFieldTypes.AssetLink;
  desktopAspectRatio?: EntryFieldTypes.EntryLink<AspectRatioSkeleton>;
  mobileAspectRatio?: EntryFieldTypes.EntryLink<AspectRatioSkeleton>;
  hyperlinkedImages?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<HyperlinkedMediaSkeleton>>;
  video?: EntryFieldTypes.EntryLink<VideoItemSkeleton | YouTubeEmbedSkeleton>;
}

export type CardSkeleton = EntrySkeletonType<CardFields, 'card'>;

export interface CardCarouselFields {
  internalName?: EntryFieldTypes.Symbol;
  sectionName?: EntryFieldTypes.Symbol;
  color?: EntryFieldTypes.Object<ColorField>;
  margins?: EntryFieldTypes.Symbol<Margins>;
  cardStyle?: EntryFieldTypes.Symbol<CardStyle>;
  cards: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<CardSkeleton>>;
}

export type CardCarouselSkeleton = EntrySkeletonType<CardCarouselFields, 'cardCarousel'>;
export type CardCarouselEntryFields = Entry<CardCarouselSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>['fields'];

export interface CardCarouselRenderProps {
  color?: Colorways;
  margins?: Margins;
  className?: RecursivePartial<CardCarouselClassNames>;
  paginationPortalRef?: React.RefObject<Element | null>;
  getCarouselPaginationThreshold?: CarouselPaginationThresholdProp;
}

type EntryNativeCardCarouselProps = Entry<CardCarouselSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> &
  WithTypename<'CardCarousel'> &
  WithStatsigFlag &
  WithSectionName;

export type CardCarouselProps = EntryNativeCardCarouselProps & CardCarouselRenderProps;
